// Fassung der Oberfläche: Pfad des Einstiegsskripts (`/assets/index-….js`) im geladenen Dokument und in einer frischen
// Antwort des Servers auf `/`. Weichen beide ab, liegt eine neue Fassung bereit (web/shell/pwa.ts); kommt gar keine
// Antwort, ist der Server nicht erreichbar (web/api/pages/states.tsx, Seite ließ sich nicht nachladen).

/** Pfad des Einstiegsskripts in einem HTML-Text. */
export function entryScriptOf(html: string): string | null {
  const match =
    /<script\b[^>]*\btype=["']module["'][^>]*\bsrc=["']([^"']*\/assets\/index-[^"']+\.js)["']/i.exec(html) ??
    /<script\b[^>]*\bsrc=["']([^"']*\/assets\/index-[^"']+\.js)["'][^>]*\btype=["']module["']/i.exec(html)
  return match?.[1] ?? null
}

/** Pfad des Einstiegsskripts im laufenden Dokument (null im Entwicklungsmodus ohne gebaute Fassung). */
export function loadedEntryScript(): string | null {
  if (typeof document === 'undefined') return null
  const script = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')
  if (!script) return null
  try {
    return new URL(script.src, window.location.href).pathname
  } catch {
    return script.getAttribute('src')
  }
}

export type ServerVersion =
  /** keine Antwort (Netz weg, Server aus) */
  | { reachable: false }
  /** Antwort da; `entry` ist das Einstiegsskript der Fassung, die der Server jetzt ausliefert */
  | { reachable: true; entry: string | null }

/**
 * Fragt erst `/api/health` (geht nie durch einen Service Worker), dann `/` ohne Cache. Wirft nie; nach `timeoutMs`
 * gilt der Server als nicht erreichbar.
 */
export async function fetchServerVersion(timeoutMs = 8000): Promise<ServerVersion> {
  const controller = typeof AbortController === 'undefined' ? null : new AbortController()
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined
  try {
    const health = await fetch('/api/health', { cache: 'no-store', credentials: 'same-origin', ...(controller ? { signal: controller.signal } : {}) })
    if (health.status >= 500) return { reachable: false }
    const response = await fetch('/', {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { accept: 'text/html' },
      ...(controller ? { signal: controller.signal } : {}),
    })
    // 502/503 vom Proxy davor: Der Server selbst antwortet nicht
    if (!response.ok) return response.status >= 500 ? { reachable: false } : { reachable: true, entry: null }
    const entry = entryScriptOf(await response.text())
    return { reachable: true, entry: entry ? new URL(entry, window.location.href).pathname : null }
  } catch {
    return { reachable: false }
  } finally {
    if (timer) clearTimeout(timer)
  }
}
